import React, { useState, useEffect } from 'react';
import { useToast } from '../../../Toaster/ToastContext';
import PropTypes from 'prop-types';
import { StoreSubscriptionPlans } from '../../../services/subscriptionService';

export default function AddPlan ({ plan, onSave })  {
    const { showToast } = useToast();
    const [formData, setFormData] = useState({
        name: '',
        featured_plan: false,
        color: '#000000',
        short_description: '',
        no_of_users: '',
        interval: 0,
        free_plan: false,
        price: 0,
        custom_features: [],
    });

    const [errors, setErrors] = useState({
        name: '',
        color: '',
        short_description: '',
        no_of_users: '',
        interval: '',
    });

    useEffect(() => {
        if (plan) {
            setFormData({
                name: plan.name || '',
                featured_plan: plan.featured_plan || false,
                color: plan.color || '#000000',
                short_description: plan.short_description || '',
                no_of_users: plan.no_of_users || '',
                interval: plan.interval || 0,
                free_plan: plan.free_plan || false,
                price: plan.price || 0,
                custom_features: Array.isArray(plan.custom_features) ? plan.custom_features : [],
            });
        } else {
            setFormData({
                name: '',
                featured_plan: false,
                color: '#000000',
                short_description: '',
                no_of_users: '',
                interval: 0,
                free_plan: false,
                price: 0,
                custom_features: [],
            });
        }
        setErrors({
            name: '',
            color: '',
            short_description: '',
            no_of_users: '',
            interval: '',
        });
    }, [plan]);

    const validateField = (name, value) => {
        switch (name) {
            case 'name':
                return value.trim() ? '' : 'Plan name is required';
            case 'color':
                return value ? '' : 'Plan badge color is required';
            case 'short_description':
                return value.trim() ? '' : 'Short description is required';
            case 'no_of_users':
                return value && !isNaN(value) && parseInt(value) > 0 ? '' : 'Number of users must be a positive number';
            case 'interval':
                return value !== '' ? '' : 'Plan interval is required';
            default:
                return '';
        }
    };

    const validateForm = () => {
        const newErrors = {
            name: validateField('name', formData.name),
            color: validateField('color', formData.color),
            short_description: validateField('short_description', formData.short_description),
            no_of_users: validateField('no_of_users', formData.no_of_users),
            interval: validateField('interval', formData.interval),
        };
        setErrors(newErrors);
        return (
            !Object.values(newErrors).some((error) => error !== '') 
        );
    };

    const addCustomFeature = () => {
        setFormData((prev) => ({
            ...prev,
            custom_features: [...prev.custom_features, { name: '' }],
        }));
    };

    const removeCustomFeature = (index) => {
        setFormData((prev) => ({
            ...prev,
            custom_features: prev.custom_features.filter((_, i) => i !== index),
        }));
    };

    const toggleOptions = [
        { label: 'Dashboard', controlName: 'dashboard' }
    ];

    const handleFeatureChange = (index, value) => {
        const updatedFeatures = [...formData.custom_features];
        updatedFeatures[index] = { ...updatedFeatures[index], name: value };
        setFormData((prev) => ({ ...prev, custom_features: updatedFeatures }));
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
        setErrors((prev) => ({
            ...prev,
            [name]: validateField(name, type === 'checkbox' ? checked : value),
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            try {
                const response = await StoreSubscriptionPlans({
                    ...formData,
                    ...(plan && { id: plan.id }),
                });
                if (response.ok) {
                    onSave && onSave();
                    setFormData({
                        name: '',
                        featured_plan: false,
                        color: '#000000',
                        short_description: '',
                        no_of_users: '',
                        interval: 0,
                        free_plan: false,
                        price: 0,
                        custom_features: [],
                        dashboard: false, // Added to match toggleOptions
                    });
                    setErrors({
                        name: '',
                        color: '',
                        short_description: '',
                        no_of_users: '',
                        interval: '',
                    });
                    document.getElementById('add-plan').querySelector('[data-bs-dismiss="modal"]').click();
                    showToast('Plan added successfully', 'success');
                } else {
                    showToast('Failed to add plan', 'error');
                }
            } catch (error) {
                showToast('Error adding plan', 'error');
            }
        }
    };

    const togglePrice = (e) => {
        const isFree = e.target.checked;
        setFormData((prev) => ({
            ...prev,
            free_plan: isFree,
            price: isFree ? 0 : prev.price,
        }));
    };

    return (
        <div className="modal fade" id="add-plan">
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content">
                    <div className="modal-header border-0 custom-modal-header d-flex justify-content-between align-items-center">
                        <div className="page-title">
                            <h4>{plan ? 'Update Subscription Plan' : 'Add Subscription Plan'}</h4>
                        </div>
                        <button type="button" className="close" data-bs-dismiss="modal" aria-label="Close">
                            <span aria-hidden="true">×</span>
                        </button>
                    </div>
                    <div className="modal-body">
                        <form onSubmit={handleSubmit}>
                            <div className="card subscription-card mb-4" style= {{border: '1px solid #dbe0e6'}}>
                                <ul className="list-group list-group-flush">
                                    <li className="list-group-item">
                                        <div className="row g-2 align-items-center pb-2">
                                            <div className="col-12 col-lg-4">
                                                <label className="col-form-label"><strong>Plan Name : <span className="text-danger">*</span></strong></label>
                                            </div>

                                            <div className="col-12 col-lg-4">
                                                <input type="checkbox" name="featured_plan" checked={formData.featured_plan}
                                                    onChange={handleChange} className="form-check-input"/>
                                                <label className="ms-2">Featured plan</label>
                                            </div>

                                            <div className="col-12 col-lg-4">
                                                <input type="text" name="name" value={formData.name} onChange={handleChange}
                                                    className={`form-control ${errors.name ? 'is-invalid' : ''}`} placeholder="Enter plan name" required
                                                />
                                                {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                            </div>
                                            
                                        </div>
                                    </li>

                                    <li className="list-group-item">
                                        <div className="row g-2 align-items-center pb-2">
                                            <div className="col-12 col-lg-8">
                                                <label className="col-form-label"><strong>Plan badge color : <span className="text-danger">*</span></strong></label>
                                            </div>

                                            <div className="col-12 col-lg-4">
                                                <div className="input-group">
                                                    <input type="color" name="color" value={formData.color} onChange={handleChange}
                                                        className={`form-control color-picker ${errors.color ? 'is-invalid' : ''}`} style={{ padding: '0' }} required
                                                    />
                                                    {errors.color && <div className="invalid-feedback">{errors.color}</div>}
                                                </div>
                                            </div>
                                        </div>
                                    </li>

                                    <li className="list-group-item">
                                        <div className="row g-2 align-items-center pb-2">
                                            <div className="col-12 col-lg-8">
                                                <label className="col-form-label"><strong>Short description : <span className="text-danger">*</span></strong></label>
                                            </div>
                                            <div className="col-12 col-lg-4">
                                                <textarea name="short_description" value={formData.short_description} onChange={handleChange}
                                                    className={`form-control ${errors.short_description ? 'is-invalid' : ''}`} placeholder="Max 150 character" maxLength="150" required
                                                />
                                                {errors.short_description && (
                                                    <div className="invalid-feedback">{errors.short_description}</div>
                                                )}
                                            </div>
                                        </div>
                                    </li>

                                    <li className="list-group-item">
                                        <div className="row g-2 align-items-center pb-2">
                                            <div className="col-12 col-lg-8">
                                                <label className="col-form-label"><strong>Number of Users : <span className="text-danger">*</span></strong></label>
                                            </div>
                                            <div className="col-12 col-lg-4">
                                                <input type="number" name="no_of_users" value={formData.no_of_users} onChange={handleChange}
                                                    className={`form-control ${errors.no_of_users ? 'is-invalid' : ''}`}
                                                    placeholder="Enter number of users" min="1" required
                                                />
                                                {errors.no_of_users && (
                                                    <div className="invalid-feedback">{errors.no_of_users}</div>
                                                )}
                                            </div>
                                        </div>
                                    </li>

                                    <li className="list-group-item">
                                        <div className="row g-2 align-items-center pb-2">
                                            <div className="col-12 col-lg-8">
                                                <label className="col-form-label"><strong>Plan Interval : <span className="text-danger">*</span></strong></label>
                                            </div>
                                            <div className="col-12 col-lg-4">
                                                <select name="interval" value={formData.interval}
                                                    onChange={handleChange} className={`form-select ${errors.interval ? 'is-invalid' : ''}`} required
                                                >
                                                    <option value={0}>Monthly</option>
                                                    <option value={1}>Yearly</option>
                                                    <option value={2}>Lifetime</option>
                                                </select>
                                                {errors.interval && <div className="invalid-feedback">{errors.interval}</div>}
                                            </div>
                                        </div>
                                    </li>

                                    <li className="list-group-item">
                                        <div className="row g-2 align-items-center pb-2">
                                            <div className="col-12 col-lg-6">
                                                <label className="col-form-label"><strong>Plan Price : <span className="text-danger">*</span></strong></label>
                                            </div>
                                            <div className="col-12 col-lg-2">
                                                <input type="checkbox" onChange={togglePrice}
                                                    checked={formData.free_plan} className="form-check-input" />
                                                <label className="ms-2">Free</label>
                                            </div>
                                            <div className="col-12 col-lg-4">
                                                <div className="input-group">
                                                    <input type="number" name="price" value={formData.price} onChange={handleChange}
                                                        className="form-control" placeholder="0.00"
                                                        step="0.01" disabled={formData.free_plan}
                                                    />
                                                    <span className="input-group-text"><strong>$ USD</strong></span>
                                                </div>
                                            </div>
                                        </div>
                                    </li>

                                    {toggleOptions.map((toggle, index) => (
                                        <li className="list-group-item" key={index}>
                                            <div className="row align-items-center">
                                                <div className="col-8 col-lg-8">
                                                    <label className="col-form-label"><strong>{toggle.label} :</strong></label>
                                                </div>
                                                <div className="col-4 col-lg-4">
                                                    <div className="form-check form-switch">
                                                        <input type="checkbox" name={toggle.controlName} checked={formData[toggle.controlName]}
                                                            onChange={handleChange} className="form-check-input"
                                                        />
                                                        <label className="form-check-label">
                                                            {formData[toggle.controlName] ? 'ON' : 'OFF'}
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {formData.custom_features.length > 0 && (
                                <div className="card subscription-card mb-3">
                                    <div className="bg-secondary text-white table-header" style={{padding: '6px'}}>
                                        Custom features
                                    </div>
                                    <ul className="list-group list-group-flush">
                                        {formData.custom_features.map((feature, index) => (
                                            <li className="list-group-item" key={index}>
                                                <div className="row g-2 align-items-center">
                                                    <div className="col-12 col-lg-8">
                                                        <label className="col-form-label">
                                                            <strong>Feature name : <span className="text-danger">*</span></strong>
                                                        </label>
                                                    </div>
                                                    <div className="col-12 col-lg-4">
                                                        <div className="d-flex align-items-center">
                                                            <input type="text" value={feature.name} onChange={(e) => handleFeatureChange(index, e.target.value)}
                                                                className={`form-control me-2 ${!feature.name ? 'is-invalid' : ''}`} placeholder="Enter name" 
                                                                required autoFocus={index === formData.custom_features.length - 1}
                                                            />
                                                            <button type="button" className="btn btn-danger btn-sm" onClick={() => removeCustomFeature(index)}>
                                                                <i className="fas fa-trash"></i>
                                                            </button>
                                                        </div>
                                                        {!feature.name && (
                                                            <div className="text-danger mt-1">
                                                                Feature name is required
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div className="card custom-card mb-3">
                                <div className="card-body">
                                    <div className="row g-2 align-items-center">
                                        <div className="col-12 col-lg-4">
                                            <button type="button" className="btn btn-primary" onClick={addCustomFeature}>
                                                <i className="fas fa-plus me-1"></i> Add Feature
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer-btn">
                                <button type="button" className="btn btn-cancel me-2" data-bs-dismiss="modal">
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-submit">
                                    {plan ? 'Update Plan' : 'Add Plan'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    )
}

AddPlan.propTypes = {
    plan: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        featured_plan: PropTypes.bool,
        color: PropTypes.string,
        short_description: PropTypes.string,
        no_of_users: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        interval: PropTypes.number,
        free_plan: PropTypes.bool,
        price: PropTypes.number,
        custom_features: PropTypes.arrayOf(PropTypes.shape({
            name: PropTypes.string
        }))
    }),
    onSave: PropTypes.func
};

AddPlan.defaultProps = {
    plan: null,
};